<template>
  <div>
    <v-row>
      <v-col cols="12" md="3" lg="2">
        <v-card outlined tile>
          <v-card-title>
            Trainers
          </v-card-title>
          <v-list dense>
            <v-list-item-group v-model="selectedTrainer">
              <v-list-item v-for="trainer of trainerList" :key="trainer._id" three-line>
                <v-list-item-content>
                  <v-list-item-title>{{ trainer.person.name }}</v-list-item-title>
                </v-list-item-content>

                <v-list-item-action>
                  <v-btn
                    icon
                    v-if="trainer"
                    @click.stop="editPerson(trainer.personId)"
                  >
                    <v-icon>open_in_new</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list-item-group>
          </v-list>
          <v-card-actions>
            <v-btn
              text
              color="primary"
              @click="addTrainer"
            >
              <v-icon>add</v-icon>
              Add trainer
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
      <v-col cols="12" md="9" lg="10">
        <v-row>
          <v-col cols="12">
            <v-card outlined tile>
              <v-card-title>
                Details
                <v-spacer/>
                <v-btn
                  @click="editTrainer(trainer)"
                  icon
                >
                  <v-icon>edit</v-icon>
                </v-btn>
              </v-card-title>
              <template v-if="trainer">
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Training area (state)</v-list-item-title>
                    <v-list-item-subtitle>{{ trainer.trainingState }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Approved to conduct competency checks?</v-list-item-title>
                    <v-list-item-subtitle>{{ trainer.approvedForCompetencyVerification | yesNo }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item v-if="trainer.competencyVerifiedBy">
                  <v-list-item-content>
                    <v-list-item-title>Competency verified by</v-list-item-title>
                    <v-list-item-subtitle>{{ trainer.competencyVerifiedBy.name }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Current?</v-list-item-title>
                    <v-list-item-subtitle>{{ trainer.isCurrent | yesNo }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-card>
          </v-col>
          <v-col cols="12">
            <v-card outlined tile>
              <v-card-title>Sessions</v-card-title>
              <template v-if="trainer">
                <v-card-text v-if="!sessions.length">
                  {{ $apollo.queries.sessions.loading ? 'Loading...' : 'No sessions found.' }}
                </v-card-text>
                <v-list-item v-for="session of sessions" :key="session._id" two-line>
                  <v-list-item-content>
                    <v-list-item-title>{{ _.get(session, 'module.code') }} - {{ session.scheduledDate | formatDate }}</v-list-item-title>
                    <v-list-item-subtitle>Venue: {{ session.venue || 'No venue' }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <v-dialog
      v-model="trainerDialog"
      width="800"
    >
      <v-card>
        <v-card-title class="headline grey lighten-2">
          {{ trainerForm._id ? 'Edit' : 'New' }} trainer
        </v-card-title>

        <v-card-text>
          <v-form>
            <v-switch
              v-if="!trainerForm._id"
              v-model="newPerson"
              label="Create new Person"
            />
            <person-search-field
              v-show="!newPerson"
              v-model="trainerForm.personId"
              v-bubble.error
              :exclude="trainers.map(o => o.personId)"
            />
            <person-form
              v-show="newPerson"
              v-model="personForm"
              @valid="personValid = $event"
            />
            <v-select
              label="Training area (state)"
              name="trainingState"
              v-model="trainerForm.trainingState"
              :items="Object.values(AddressState)"
            />
            <v-select
              label="Competency verified by"
              name="competencyVerifiedByPersonId"
              v-model="trainerForm.competencyVerifiedByPersonId"
              :items="verifierList"
              item-text="person.name"
              item-value="personId"
            />
            <v-checkbox
              label="Approved to conduct competency checks"
              name="approvedForCompetencyVerification"
              v-model="trainerForm.approvedForCompetencyVerification"
            />
            <v-checkbox
              label="Current"
              name="isCurrent"
              v-model="trainerForm.isCurrent"
            />
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-btn
            color="primary"
            text
            @click="saveTrainer"
            :disabled="!canSaveTrainer"
          >
            Save
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="trainerDialog = false"
          >
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import gql from 'graphql-tag'
import { sortBy } from 'lodash'
import { AddressState } from '@mymsa/common'

import PersonSearchField from './inputs/PersonSearchField.vue'
import PersonForm from './forms/PersonForm.vue'

export default {
  name: 'TrainerList',

  components: {
    PersonSearchField,
    PersonForm
  },

  data: () => ({
    AddressState,
    trainers: [],
    selectedTrainer: null,
    sessions: [],
    trainerForm: {},
    trainerDialog: false,
    verifiers: [],
    newPerson: false,
    personForm: {},
    personValid: false
  }),

  computed: {
    trainer () {
      return this.trainerList[this.selectedTrainer]
    },

    trainerList () {
      return sortBy(this.trainers, ['person.name'])
    },

    verifierList () {
      return sortBy(this.verifiers, ['person.name'])
    },

    canSaveTrainer () {
      return this.newPerson
        ? this.personValid
        : !!this.trainerForm.personId
    }
  },

  apollo: {
    trainers: {
      query: gql`query {
        trainers: trainerMany(limit: 0) {
          _id
          personId
          trainingState
          isCurrent
          approvedForCompetencyVerification
          competencyVerifiedByPersonId
          person {
            _id
            name
            email
            phoneNumber
          }
          competencyVerifiedBy {
            _id
            name
          }
        }
      }`,
      error (e) { this.onError(e) }
    },

    verifiers: {
      query: gql`query {
        verifiers: trainerMany(limit: 0, filter: {
          isCurrent: true
          approvedForCompetencyVerification: true
        }) {
          _id
          personId
          person {
            _id
            name
          }
        }
      }`,
      error (e) { this.onError(e) }
    },

    sessions: {
      query: gql`query TrainerSessions ($filter: FilterFindManyTrainingSessionInput) {
        sessions: trainingSessionMany(limit: 0, sort: _ID_DESC, filter: $filter) {
          _id
          scheduledDate
          venue
          module {
            _id
            code
          }
        }
      }`,
      variables () {
        return {
          filter: {
            trainerId: this.trainer._id
          }
        }
      },
      error (e) { this.onError(e) },
      skip () {
        return !this.trainer
      }
    }
  },

  methods: {
    onError (e) {
      this.$emit('error', e)
    },

    addTrainer () {
      this.trainerForm = {}
      this.personForm = {}
      this.newPerson = false
      this.trainerDialog = true
      this.selectedTrainer = null
    },

    editTrainer (trainer) {
      this.newPerson = false
      this.trainerForm = {
        _id: trainer._id,
        personId: trainer.personId,
        isCurrent: trainer.isCurrent,
        trainingState: trainer.trainingState,
        competencyVerifiedByPersonId: trainer.competencyVerifiedByPersonId,
        approvedForCompetencyVerification: trainer.approvedForCompetencyVerification
      }
      this.trainerDialog = true
    },

    editPerson (personId) {
      const route = this.$router.resolve({ name: 'AdminPerson', params: { id: personId } })
      window.open(route.href, '_blank')
    },

    async saveTrainer () {
      let mutation
      const id = this.trainerForm._id
      const record = {
        personId: this.trainerForm.personId,
        isCurrent: this.trainerForm.isCurrent,
        trainingState: this.trainerForm.trainingState,
        competencyVerifiedByPersonId: this.trainerForm.competencyVerifiedByPersonId,
        approvedForCompetencyVerification: this.trainerForm.approvedForCompetencyVerification
      }

      try {
        if (this.newPerson) {
          const newPerson = await this.$apollo.mutate({
            mutation: gql`mutation ($record: CreateOnePersonInput!) {
              personCreateOne(record: $record) {
                recordId
              }
            }`,
            variables: {
              record: this.personForm
            }
          })
          record.personId = newPerson.data.personCreateOne.recordId
        }

        if (id) {
          mutation = gql`mutation ($id: MongoID!, $record: UpdateByIdTrainerInput!) {
            trainerUpdateById(_id: $id, record: $record) { recordId }
          }`
        } else {
          mutation = gql`mutation ($record: CreateOneTrainerInput!) {
            trainerCreateOne(record: $record) { recordId }
          }`
        }

        await this.$apollo.mutate({
          mutation,
          variables: {
            id,
            record
          }
        })
        await this.$apollo.queries.trainers.refresh()
        await this.$apollo.queries.verifiers.refresh()
      } catch (e) {
        this.onError(e)
        return
      }
      this.trainerDialog = false
      this.$emit('info', 'Trainer saved.')
    }
  }
}
</script>
